import { motion } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT

import { Service } from '../../../shared/hooks/useServiceSelection';
import LoadingComponent from '../../../shared/components/LoadingComponent';
import FooterNavigation from '../../../shared/components/Navigation';
import { useDateTimeData } from '../../../shared/hooks/useDateTimeData';

interface SimpleDateSelectionProps {
  selectedServices: Service[];
  selectedTimeZone: string;
  selectedLocation: any;
  // Add these props to match what the wrapper sends
  selectedDate: Date | null;
  setSelectedDate: (date: Date) => void;
  selectedTime: string | null;
  setSelectedTime: (time: string) => void;
  onNext: () => void;
  onPrev: () => void;
  nodeData?: any;
}

const SimpleDateSelection: React.FC<SimpleDateSelectionProps> = ({
  selectedServices,
  selectedTimeZone,
  selectedLocation,
  // Accept the passed props
  selectedDate: propSelectedDate,
  setSelectedDate: propSetSelectedDate,
  selectedTime: propSelectedTime,
  setSelectedTime: propSetSelectedTime,
  onNext,
  onPrev,
  nodeData
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  // Add state for visible date range and dynamic date generation
  const [visibleDatesOffset, setVisibleDatesOffset] = useState(0);
  const DATES_PER_PAGE = 7; // Number of dates visible at once - changed from 5 to 7

  // Generate dates dynamically for infinite future navigation
  const [extendedDates, setExtendedDates] = useState<Date[]>([]);

  // Extract settings from nodeData
  const settings = nodeData?.settings || {};
  const colors = settings?.colors || {
    cardBackground: '#000000',
    cardBorder: 'rgba(221,184,137,0.1)',
    titleText: '#DDB889',
    iconColor: '#DDB889',
    dateBackground: 'rgba(0,0,0,0.95)',
    dateBorder: 'rgba(221,184,137,0.1)',
    dateText: '#DDB889',
    selectedDateBackground: '#DDB889',
    selectedDateBorder: '#DDB889',
    selectedDateText: '#000000',
    timeslotBackground: 'rgba(0,0,0,0.95)',
    timeslotBorder: 'rgba(221,184,137,0.1)',
    timeslotText: '#DDB889',
    selectedTimeslotBackground: '#DDB889',
    selectedTimeslotText: '#000000',
    summaryBackground: 'rgba(221,184,137,0.05)',
    summaryBorder: 'rgba(221,184,137,0.1)',
    summaryText: '#DDB889',
    buttonBackground: '#DDB889',
    buttonText: '#000000'
  };

  const showTimeSlots = settings?.showTimeSlots !== false; // Default to true

  // Use the shared hook for date/time data
  const {
    availableDates,
    fetching,
    error,
    timeGroups,
    formatDisplayDate,
    setSelectedDate,
  } = useDateTimeData({
    selectedServices,
    selectedTimeZone,
    selectedLocation
  });

  // Initialize extended dates or update when availableDates changes
  useEffect(() => {
    if (availableDates && availableDates.length > 0) {
      // Start with available dates from the hook
      const initialDates = [...availableDates];

      // Generate additional future dates if needed
      const lastDate = new Date(initialDates[initialDates.length - 1]);
      const additionalDates = [];

      // Add 30 more future dates (adjust as needed)
      for (let i = 1; i <= 30; i++) {
        const nextDate = new Date(lastDate);
        nextDate.setDate(lastDate.getDate() + i);
        additionalDates.push(nextDate);
      }

      setExtendedDates([...initialDates, ...additionalDates]);
    }
  }, [availableDates]);

  // Format date helper functions
  const formatDay = (date: Date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const formatDate = (date: Date) => {
    return date.getDate();
  };

  // Prepare time slots for display
  const getTimeSlots = () => {
    if (!timeGroups) return [];

    const allSlots = [
      ...(timeGroups.morning || []),
      ...(timeGroups.afternoon || []),
      ...(timeGroups.evening || [])
    ];

    return allSlots.sort();
  };

  const timeSlots = getTimeSlots();

  // Use the props passed from parent instead of values from hook
  const handleDateSelect = (date: Date) => {
    propSetSelectedDate(date);
    setSelectedDate(date);
  };

  const handleTimeSelect = (time: string) => {
    propSetSelectedTime(time);
  };

  // Navigation functions for date pagination
  const handlePrevDates = () => {
    if (visibleDatesOffset > 0) {
      setVisibleDatesOffset(prev => prev - 1);
    }
  };

  const handleNextDates = () => {
    // Always allow moving forward for infinite pagination
    setVisibleDatesOffset(prev => prev + 1);
  };

  // Get visible dates based on current offset - using extended dates for infinite future
  const visibleDates = extendedDates.length > 0
    ? extendedDates.slice(
        visibleDatesOffset * DATES_PER_PAGE,
        (visibleDatesOffset * DATES_PER_PAGE) + DATES_PER_PAGE
      )
    : [];

  // Check if we can navigate further
  const canGoBack = visibleDatesOffset > 0;
  const canGoForward = true; // Always allow forward navigation for infinite future dates

  // Add more future dates when approaching the end of extended dates
  useEffect(() => {
    if (extendedDates.length === 0) return;

    const remainingDates = extendedDates.length - ((visibleDatesOffset + 1) * DATES_PER_PAGE);
    if (remainingDates < 10) {
      const lastDate = new Date(extendedDates[extendedDates.length - 1]);
      const additionalDates: Date[] = [];

      for (let i = 1; i <= 30; i++) {
        const nextDate = new Date(lastDate);
        nextDate.setDate(lastDate.getDate() + i);
        additionalDates.push(nextDate);
      }

      setExtendedDates(prevDates => [...prevDates, ...additionalDates]);
    }
  }, [visibleDatesOffset, extendedDates.length]);

  // Generate title based on whether time slots are shown
  const getTitle = () => {
    if (showTimeSlots) {
      return t('booking.selectDateTime'); // CHANGED: "Select Date and Time"
    }
    return t('datetime.selectDateOnly'); // CHANGED: "Select Date"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="lp-w-full lp-transform lp-scale-100"
    >
      <div
        className="lp-rounded-xl lp-overflow-hidden lp-p-4"
        style={{
          backgroundColor: colors.cardBackground,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: colors.cardBorder
        }}
      >
        <div className="lp-flex lp-items-center lp-justify-center lp-mb-4">
          <h3
            className="lp-text-lg lp-font-medium lp-text-center lp-flex lp-items-center"
            style={{ color: colors.titleText }}
          >
            <Calendar
              className="lp-mr-2"
              size={18}
              style={{ color: colors.iconColor }}
            />
            {getTitle()} {/* CHANGED: Dynamic title based on showTimeSlots */}
          </h3>
        </div>

        {/* Date Selection with Navigation Arrows - Full Width */}
        <div className="lp-mb-4">
          <div className="lp-flex lp-items-center lp-justify-between lp-w-full">
            {/* Left Arrow */}
            <motion.div
              className={`lp-flex lp-items-center lp-justify-center lp-w-10 lp-h-10 lp-rounded-full ${canGoBack ? 'lp-cursor-pointer' : 'lp-opacity-50 lp-cursor-not-allowed'}`}
              style={{
                backgroundColor: colors.dateBackground,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: colors.dateBorder,
                color: colors.dateText
              }}
              onClick={canGoBack ? handlePrevDates : undefined}
              whileHover={canGoBack ? { scale: 1.1 } : undefined}
              whileTap={canGoBack ? { scale: 0.95 } : undefined}
            >
              <ChevronLeft size={20} />
            </motion.div>

            {/* Date Buttons - Full Width */}
            <div className="lp-flex lp-justify-between lp-w-full lp-mx-2">
              {visibleDates.length > 0 ? visibleDates.map((date, index) => (
                <motion.div
                  key={index}
                  className="lp-flex-1 lp-mx-1 lp-h-20 lp-flex lp-flex-col lp-items-center lp-justify-center lp-rounded-lg lp-cursor-pointer"
                  style={{
                    backgroundColor: propSelectedDate && date.toDateString() === propSelectedDate.toDateString()
                      ? colors.selectedDateBackground
                      : colors.dateBackground,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: propSelectedDate && date.toDateString() === propSelectedDate.toDateString()
                      ? colors.selectedDateBorder
                      : colors.dateBorder,
                    color: propSelectedDate && date.toDateString() === propSelectedDate.toDateString()
                      ? colors.selectedDateText
                      : colors.dateText
                  }}
                  onClick={() => handleDateSelect(date)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="lp-text-sm lp-font-medium">{formatDay(date)}</div>
                  <div className="lp-text-xl lp-font-bold">{formatDate(date)}</div>
                  <div className="lp-text-xs">
                    {date.toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                </motion.div>
              )) : (
                <div className="lp-flex lp-items-center lp-justify-center lp-w-full lp-py-4" style={{ color: colors.dateText }}>
                  <LoadingComponent />
                </div>
              )}
            </div>

            {/* Right Arrow */}
            <motion.div
              className="lp-flex lp-items-center lp-justify-center lp-w-10 lp-h-10 lp-rounded-full lp-cursor-pointer"
              style={{
                backgroundColor: colors.dateBackground,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: colors.dateBorder,
                color: colors.dateText
              }}
              onClick={handleNextDates}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronRight size={20} />
            </motion.div>
          </div>
        </div>

        {/* Time Slots - Only if showTimeSlots is true */}
        {showTimeSlots && (
          <div className="lp-mb-4">
            {fetching ? (
              <div className="lp-h-24 lp-flex lp-items-center lp-justify-center">
                <LoadingComponent />
              </div>
            ) : error ? (
              <div className="lp-text-center lp-text-red-500">
                {t('datetime.errorLoadingTimeSlots')} {/* CHANGED: "Error loading time slots" */}
              </div>
            ) : timeSlots.length > 0 ? (
              <div className="lp-grid lp-grid-cols-4 lp-gap-3">
                {timeSlots.map((slot) => (
                  <motion.div
                    key={slot}
                    className="lp-relative lp-p-3 lp-rounded-lg lp-flex lp-items-center lp-justify-center lp-text-sm lp-cursor-pointer"
                    style={{
                      backgroundColor: propSelectedTime === slot
                        ? colors.selectedTimeslotBackground
                        : colors.timeslotBackground,
                      borderWidth: propSelectedTime === slot ? 0 : 1,
                      borderStyle: 'solid',
                      borderColor: colors.timeslotBorder,
                      color: propSelectedTime === slot
                        ? colors.selectedTimeslotText
                        : colors.timeslotText
                    }}
                    onClick={() => handleTimeSelect(slot)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="lp-flex lp-items-center">
                      <Clock
                        className="lp-mr-2"
                        size={14}
                        style={{ color: 'currentColor' }}
                      />
                      {slot}
                    </span>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="lp-text-center" style={{ color: colors.dateText }}>
                {t('datetime.noAvailableTimeSlotsForDate')} {/* CHANGED: "No available time slots for this date" */}
              </div>
            )}
          </div>
        )}

        {/* Selected Date/Time Summary */}
        {propSelectedDate && (
          <div
            className="lp-mb-4 lp-rounded-lg lp-p-3"
            style={{
              backgroundColor: colors.summaryBackground || colors.timeslotBackground,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: colors.summaryBorder || colors.timeslotBorder
            }}
          >
            <div className="lp-flex lp-justify-between lp-items-center">
              <div className="lp-text-sm" style={{ color: colors.summaryText || colors.timeslotText }}>
                <div className="lp-font-medium">{t('datetime.selectedDate')}:</div> {/* CHANGED: "Selected Date:" */}
                <div>{formatDisplayDate(propSelectedDate)}</div>
              </div>
              {showTimeSlots && propSelectedTime && (
                <div className="lp-text-sm lp-text-right" style={{ color: colors.summaryText || colors.timeslotText }}>
                  <div className="lp-font-medium">{t('datetime.selectedTime')}:</div> {/* CHANGED: "Selected Time:" */}
                  <div>{propSelectedTime}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation Footer */}
        <FooterNavigation
          onPrev={onPrev}
          onNext={onNext}
          disabled={!propSelectedDate || (showTimeSlots && !propSelectedTime)}
        />
      </div>
    </motion.div>
  );
};

export default SimpleDateSelection;
